import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { TeamMember } from '.';
import { ReactComponent as ChevronUpIcon } from '../../../../shared/icons/chevron-up.svg';
import { ReactComponent as ChevronDownIcon } from '../../../../shared/icons/chevron-down.svg';
import Avatar from '../../../avatar';
import styles from './styles.module.scss';
import { useAuth } from '../../../../auth';
import { useContext, useState } from 'react';
import PermissionIcon from '../../../permission-icon';
import EditPermissionsList from '../../../editPermissionsList';
import { A, paths } from '../../../../routing';
import { TeamIdContext } from '../../../../team-id-context';
import classNames from 'classnames';
import { Button } from 'react-bootstrap';
import ManageDealAccessSlideout from '../../../manageDealAccess/ManageDealAccessSlideout';
import { RemoveTeamMemberButton } from '../../removeTeamMemberButton';

interface TeamMemberItemProps {
  member: TeamMember;
  showDealMembersOnly: boolean;
  isReadOnly: boolean;
  dealsData: any;
  whitelistPermissionId?: string;
  shouldShowDealsSelector: boolean;
  setUserToRemoveFromDeal?: (user: any) => void;
  currentUserIsAdmin?: boolean;
}

const TeamMemberItem: React.FC<TeamMemberItemProps> = ({
  member,
  showDealMembersOnly,
  dealsData,
  isReadOnly,
  whitelistPermissionId,
  shouldShowDealsSelector,
  setUserToRemoveFromDeal,
  currentUserIsAdmin,
}) => {
  // @ts-ignore
  const [{ user }] = useAuth();
  const teamId = useContext(TeamIdContext);
  const [isOpen, setIsOpen] = useState(false);
  const [showManageDealAccess, setShowManageDealAccess] = useState(false);

  const renderMessage = () => {
    if (isReadOnly)
      return (
        <FormattedMessage
          id="phrase.view-permissions-adjust-later"
          defaultMessage="These are your team member’s current permissions. You can adjust these later in your {team_settings}."
          values={{
            team_settings: (
              <A href={paths.team.landing} hrefParams={{ teamId }}>
                <FormattedMessage id="team_settings" defaultMessage="team settings" />
              </A>
            ),
          }}
        />
      );

    return (
      <FormattedMessage
        id="phrase.view-or-change-team-members-permissions"
        defaultMessage="View or change this team member’s permissions"
      />
    );
  };

  const renderManageDealAccessButton = () => (
    <>
      <h3 data-testid="deals-select-header">
        <FormattedMessage id="title.deals" defaultMessage="Deals" />
      </h3>
      <p>
        <FormattedMessage
          id="phrase.select-team-member-deals"
          defaultMessage="Select the deals this team member can work on."
        />
      </p>
      <Button 
        variant="primary"
        onClick={() => setShowManageDealAccess(true)}
        className="mb-3"
        disabled={!dealsData}
      >
        <FormattedMessage id="cta.manage-deal-access" defaultMessage="Manage Deal Access" />
      </Button>
      
      {dealsData && (
        <ManageDealAccessSlideout
          isOpen={showManageDealAccess}
          onClose={() => setShowManageDealAccess(false)}
          userRowData={member}
          dealsForTeam={dealsData.dealsForTeam}
          whitelistPermissionId={whitelistPermissionId || ''}
        />
      )}
    </>
  );

  return (
    <div className={classNames(styles.teamMemberItem, styles.teamMemberItemFull)}>
      <div className={styles.teamMemberItem__header} onClick={() => setIsOpen(!isOpen)}>
        <div className={styles.teamMemberItem__header__info}>
          <Avatar email={member.email} fullName={member.fullName} className="mr-3" />
          <span
            className={`${showDealMembersOnly && 'text-truncate'}`}
            style={{ width: `${showDealMembersOnly && '200px'}` }}
          >
            {member.fullName}
            {member.id === user.id && (
              <>
                {' '}
                <FormattedMessage id="phrase.hint.fullName" defaultMessage="(you)" />
              </>
            )}
          </span>
        </div>
        <div className={styles.teamMemberItem__header__actions}>
          {isOpen ? <ChevronUpIcon width={16} height={16} /> : <ChevronDownIcon width={16} height={16} />}
        </div>
      </div>
      <div className={styles.teamMemberItem__body}>
        <div className={styles.teamMemberItem__info}>
          <span className={styles.teamMemberItem__label}>Email: </span>
          <span className={styles.teamMemberItem__details}>{member.email}</span>
        </div>
        <div className={styles.teamMemberItem__info}>
          <span className={styles.teamMemberItem__label}>Permissions: </span>
          <div className={classnames(styles.teamMemberItem__details, 'd-flex')}>
            {member.permissions.map((permission: any, index: number) => (
              <div key={`${member.id}-${permission.key}-${index}`}>
                <PermissionIcon permission={permission} className="mr-1" />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div>
        {isOpen && (
          <div className={styles.teamMemberItem__expanded}>
            <h3 data-testid="team-member-row_header">
              <FormattedMessage id="title.permissions" defaultMessage="Permissions" />
            </h3>
            {renderMessage()}
            <EditPermissionsList
              data-testid="edit-permissions-list"
              permissions={member.permissions}
              dealIds={member.dealIds}
              userId={member.id}
              readOnly={!!member.isAdmin}
              isAdminUser={!!member.isAdmin}
              invitationId={member.invitationId}
              isYou={user.id === member.id}
            />
            {shouldShowDealsSelector && renderManageDealAccessButton()}
            {/* Add Remove from Deal button for mobile - same logic as desktop */}
            {showDealMembersOnly && 
             setUserToRemoveFromDeal && 
             !member.isAdmin && 
             !member.invitationId && 
             member.id !== user.id && (
              <RemoveTeamMemberButton
                data-testid="remove-team-member-from-deal-button"
                onClick={() => setUserToRemoveFromDeal(member)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamMemberItem;
